const capabilities = [
  {
    code: 'C-01',
    title: 'Graph indexing',
    body: 'Structures the codebase as a knowledge graph — relationships, call paths, and module boundaries — so Compound can traverse large projects instead of grepping blindly.',
  },
  {
    code: 'C-02',
    title: 'Vector RAG',
    body: 'Embeds docs, knowledge, and code into a retrieval index. Hybrid search pulls the right passages when the team asks a concrete question across a wide corpus.',
  },
  {
    code: 'C-03',
    title: 'Semantic search',
    body: 'Meaning-aware lookup on top of graph and RAG — find the relevant handler, guide, or decision fast, even when the wording does not match the file name.',
  },
  {
    code: 'C-04',
    title: 'Project & task analysis',
    body: 'Maps the repository, domain edges, and open work on measured indexes — so every subsequent step starts from ground truth, not a cold prompt.',
  },
  {
    code: 'C-05',
    title: 'Requirements & specs',
    body: 'Turns intent into structured TZ and atomic specs your team can review, approve, and execute against.',
  },
  {
    code: 'C-06',
    title: 'Delivery planning',
    body: 'Builds an ordered plan with dependencies, waves, and acceptance checks — orchestration that survives session breaks.',
  },
  {
    code: 'C-07',
    title: 'Implementation',
    body: 'Writes and revises production code under senior review, aligned to the conventions Compound has already learned on this project.',
  },
  {
    code: 'C-08',
    title: 'Automated testing',
    body: 'Generates and runs tests — including e2e — so changes land with evidence, not hope.',
  },
  {
    code: 'C-09',
    title: 'Security review',
    body: 'Looks for information-security issues in the change surface and turns findings into actionable fix trails.',
  },
  {
    code: 'C-10',
    title: 'Stage verification',
    body: 'Exercises builds on staging environments — independent checks against the system you are about to ship.',
  },
  {
    code: 'C-11',
    title: 'Pentesting',
    body: 'Runs structured offensive passes where the method calls for them, with retest notes closing the loop.',
  },
  {
    code: 'C-12',
    title: 'Manuals & documentation',
    body: 'Produces guides, API manuals, and operational docs from the same project memory that built the software.',
  },
  {
    code: 'C-13',
    title: 'Issue triage',
    body: 'Classifies incidents and reports, routes the next move, and keeps institutional memory of what already failed and why.',
  },
  {
    code: 'C-14',
    title: 'Self-adaptation',
    body: 'Compounds method, project, working, and institutional memory — the system fits this codebase more tightly every run.',
  },
  {
    code: 'C-15',
    title: 'Living method drops',
    body: 'We track new models, tools, and approaches as the AI industry shifts — then fold them into skills that search fast, spend fewer tokens, and stay current. Monthly updates, domain packs, and breaking-change notes ship with support and training on every plan.',
  },
]

/** Capability register — father-class surface, survey grammar, no icon cards. */
export function CompoundCapabilities() {
  return (
    <section id="capabilities" className="bg-sheet">
      <div className="mx-auto max-w-[100rem] px-6 pt-6 pb-12 lg:px-8 lg:pt-7 lg:pb-16">
        <div className="cell overflow-hidden">
          <div className="flex flex-col gap-2 border-b-2 border-ink px-5 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-7">
            <h2 className="section-title text-ink">What Compound runs</h2>
            <p className="text-sm leading-relaxed text-ink-muted sm:max-w-sm sm:text-right">
              Graph, RAG, and semantic indexes first — then the full SDLC on that ground.
            </p>
          </div>

          <div className="hidden grid-cols-[5rem_14rem_1fr] gap-6 border-b border-rule bg-sheet-edge/60 px-5 py-2.5 sm:px-7 lg:grid">
            <span className="cell-label text-ink-muted">Code</span>
            <span className="cell-label text-ink-muted">Capability</span>
            <span className="cell-label text-ink-muted">In the traverse</span>
          </div>

          <div className="divide-y divide-rule">
            {capabilities.map((cap) => (
              <article
                key={cap.code}
                className="grid gap-3 px-5 py-5 sm:px-7 lg:grid-cols-[5rem_14rem_1fr] lg:gap-6 lg:py-5"
              >
                <span className="datum text-overprint">{cap.code}</span>
                <h3 className="entry-title text-ink">{cap.title}</h3>
                <p className="text-[0.9375rem] leading-relaxed text-ink-muted">
                  {cap.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
